import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Outlet, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@/components/theme-provider';
import { Navbar } from '@/components/navbar';
import { Footer } from '@/components/footer';
import { HydrationBoundary, QueryClientProvider } from '@tanstack/react-query';
import { createQueryClient } from '@/lib/react-query-config';
import { Toaster } from 'sonner';
import { Provider } from 'react-redux';
import { store } from '@/lib/store/store';
import { ReduxHydrator } from '@/components/ReduxHydrator';
import { ScrollToTopOnRouteChange } from '@/components/ScrollToTopOnRouteChange';
import ShopifyLayout from '@/layouts/ShopifyLayout';
import { PortalAuthProvider } from '@/lib/contexts/PortalAuthContext';
import RequirePortalAuth from '@/components/portal/RequirePortalAuth';
import PortalLayout from '@/components/portal/PortalLayout';
import { AuthHashRedirector } from '@/components/portal/AuthHashRedirector';

// Pages — lazy-loaded for route-level code splitting
const IndexPage = lazy(() => import('@/pages/index'));
const AboutPage = lazy(() => import('@/pages/about'));
const CartPage = lazy(() => import('@/pages/cart'));
const CheckoutPage = lazy(() => import('@/pages/checkout'));
const ContactPage = lazy(() => import('@/pages/contact'));
const FaqPage = lazy(() => import('@/pages/faq'));
const FoodPage = lazy(() => import('@/pages/food'));
const JoinPage = lazy(() => import('@/pages/join'));
const LoginPage = lazy(() => import('@/pages/login'));
const PasswordRecoveryPage = lazy(() => import('@/pages/passwordrecovery'));
const PasswordRecoveryConfirmPage = lazy(() => import('@/pages/passwordrecovery/confirm'));
const PrivacyPage = lazy(() => import('@/pages/privacy'));
const ProfilePage = lazy(() => import('@/pages/profile'));
const ReferralPage = lazy(() => import('@/pages/referral'));
const RegisterPage = lazy(() => import('@/pages/register'));
const RequestDemoPage = lazy(() => import('@/pages/request-demo'));
const SearchPage = lazy(() => import('@/pages/search'));
const TermsPage = lazy(() => import('@/pages/terms-of-conduct-topics'));

const CatalogIndexPage = lazy(() => import('@/pages/catalog/index'));
const CatalogSlugPage = lazy(() => import('@/pages/catalog/[slug]'));
const ProductsSlugPage = lazy(() => import('@/pages/products/[slug]'));
const VendorIndexPage = lazy(() => import('@/pages/vendor/index'));
const VendorSlugPage = lazy(() => import('@/pages/vendor/slug'));
const ManufacturersIndexPage = lazy(() => import('@/pages/manufacturers/index'));
const ManufacturersSlugPage = lazy(() => import('@/pages/manufacturers/[slug]'));

const OrderIndexPage = lazy(() => import('@/pages/order/index'));
const OrderSlugPage = lazy(() => import('@/pages/order/slug'));
const OrderConfirmationPage = lazy(() => import('@/pages/order/confirmation'));
const OrderPaidPage = lazy(() => import('@/pages/order/orderpaid'));
const OrderCancelPage = lazy(() => import('@/pages/order/ordercancel'));

const MyAccountPage = lazy(() => import('@/pages/myaccount/index'));
const MyAccountAddressPage = lazy(() => import('@/pages/myaccount/address'));
const MyAccountChangePasswordPage = lazy(() => import('@/pages/myaccount/changepassword'));
const MyAccountCustomerInfoPage = lazy(() => import('@/pages/myaccount/customerinfo'));

const ShopifyAppIndexPage = lazy(() => import('@/pages/apps/shopify/index'));
const ShopifyDashboardPage = lazy(() => import('@/pages/apps/shopify/dashboard'));
const ShopifyOnboardingPage = lazy(() => import('@/pages/apps/shopify/onboarding'));
const ShopifyPreferencesPage = lazy(() => import('@/pages/apps/shopify/preferences'));
const ShopifyLinkPage = lazy(() => import('@/pages/apps/shopify/link'));

const BecomeASupplierPage = lazy(() => import('@/pages/become-a-supplier'));
const PartnerDirectoryPage = lazy(() => import('@/pages/partner-directory'));
const CraftBusinessHubPage = lazy(() => import('@/pages/craft-business-hub'));
const ContractCoPackingDirectoryPage = lazy(() => import('@/pages/contract-co-packing-directory'));
const ChampionsProgramPage = lazy(() => import('@/pages/champions-program'));
const ChampionsProgramBackupPage = lazy(() => import('@/pages/champions-program-backup'));
const EventsPage = lazy(() => import('@/pages/events'));
const BlogPage = lazy(() => import('@/pages/blog'));
const BlogPostPage = lazy(() => import('@/pages/blog/[slug]'));
const PortalLoginPage = lazy(() => import('@/pages/portal/login'));
const PortalRegisterPage = lazy(() => import('@/pages/portal/register'));
const PortalDashboardPage = lazy(() => import('@/pages/portal/dashboard'));
const PortalHomePage = lazy(() => import('@/pages/portal/home'));
const PortalProfilePage = lazy(() => import('@/pages/portal/profile'));

const PortalOrdersPage = lazy(() => import('@/pages/portal/orders'));
const PortalServicesPage = lazy(() => import('@/pages/portal/services'));
const PortalApplicationsPage = lazy(() => import('@/pages/portal/applications'));
const PortalReportsPage = lazy(() => import('@/pages/portal/reports'));
const PortalDirectoryPage = lazy(() => import('@/pages/portal/directory'));
const PortalCoMarketingPage = lazy(() => import('@/pages/portal/co-marketing'));
const PortalSettingsPage = lazy(() => import('@/pages/portal/settings'));
const PortalGoLivePage = lazy(() => import('@/pages/portal/go-live'));
const PortalTempPreviewPage = lazy(() => import('@/pages/portal/temp-preview'));
const PortalForgotPasswordPage = lazy(() => import('@/pages/portal/forgot-password'));
const PortalResetPasswordPage = lazy(() => import('@/pages/portal/reset-password'));
const PortalChangePasswordPage = lazy(() => import('@/pages/portal/change-password'));
const AdminLoginPage = lazy(() => import('@/pages/admin/login'));
const AdminSetupPage = lazy(() => import('@/pages/admin/setup'));
const AdminDashboardPage = lazy(() => import('@/pages/admin/dashboard'));
const AdminBlogPage = lazy(() => import('@/pages/admin/blog'));
const AdminDirectoryPage = lazy(() => import('@/pages/admin/directory'));
const AdminUsersPage = lazy(() => import('@/pages/admin/users'));

const PopupHost = lazy(() => import('@/components/popups/PopupHost'));
const AdminPopupsPage = lazy(() => import('@/pages/admin/popups'));

function DefaultLayout() {
  return (
    <div className="flex flex-col min-h-screen overflow-x-hidden w-full max-w-[100vw]">
      <div className="flex-grow w-full min-w-0">
        <Navbar />
        <Toaster position="top-center" richColors theme="dark" />
        <Outlet />
        <Suspense fallback={null}>
          <PopupHost />
        </Suspense>
      </div>
      <Footer />
    </div>
  );
}

function ShopifyAppLayout() {
  return (
    <ShopifyLayout>
      <Outlet />
    </ShopifyLayout>
  );
}

function PortalShellLayout() {
  return (
    <RequirePortalAuth>
      <PortalLayout>
        <Outlet />
      </PortalLayout>
    </RequirePortalAuth>
  );
}

export default function App() {
  const [qc] = React.useState(() => createQueryClient());

  return (
    <Provider store={store}>
      <ReduxHydrator />
      <QueryClientProvider client={qc}>
        <HydrationBoundary state={undefined}>
          <ThemeProvider
            attribute="class"
            defaultTheme="system"
            enableSystem
            disableTransitionOnChange
          >
            <PortalAuthProvider>
            <BrowserRouter>
              <ScrollToTopOnRouteChange />
              <AuthHashRedirector />
              <Suspense fallback={<div className="min-h-screen" />}>
              <Routes>
                {/* Shopify embedded app — no Navbar/Footer */}
                <Route element={<ShopifyAppLayout />}>
                  <Route path="/apps/shopify" element={<ShopifyAppIndexPage />} />
                  <Route path="/apps/shopify/dashboard" element={<ShopifyDashboardPage />} />
                  <Route path="/apps/shopify/onboarding" element={<ShopifyOnboardingPage />} />
                  <Route path="/apps/shopify/preferences" element={<ShopifyPreferencesPage />} />
                  <Route path="/apps/shopify/link" element={<ShopifyLinkPage />} />
                </Route>

                {/* Standalone pages with own Navbar/Footer */}
                <Route path="/about" element={<AboutPage />} />

                {/* Standard pages — with Navbar/Footer */}
                <Route element={<DefaultLayout />}>
                  <Route path="/" element={<IndexPage />} />
                  <Route path="/cart" element={<CartPage />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/faq" element={<FaqPage />} />
                  <Route path="/food" element={<FoodPage />} />
                  <Route path="/join" element={<JoinPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/passwordrecovery" element={<PasswordRecoveryPage />} />
                  <Route path="/passwordrecovery/confirm" element={<PasswordRecoveryConfirmPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/referral" element={<ReferralPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/request-demo" element={<RequestDemoPage />} />
                  <Route path="/search" element={<SearchPage />} />
                  <Route path="/terms-of-conduct-topics" element={<TermsPage />} />

                  <Route path="/catalog" element={<CatalogIndexPage />} />
                  <Route path="/catalog/*" element={<CatalogSlugPage />} />
                  <Route path="/products/:slug" element={<ProductsSlugPage />} />
                  <Route path="/vendor" element={<VendorIndexPage />} />
                  <Route path="/vendor/:slug" element={<VendorSlugPage />} />
                  <Route path="/manufacturers" element={<ManufacturersIndexPage />} />
                  <Route path="/manufacturers/:slug" element={<ManufacturersSlugPage />} />

                  <Route path="/order" element={<OrderIndexPage />} />
                  <Route path="/order/:id" element={<OrderSlugPage />} />
                  <Route path="/order/:id/cancel" element={<OrderCancelPage />} />
                  <Route path="/order/confirmation" element={<OrderConfirmationPage />} />
                  <Route path="/order/orderpaid" element={<OrderPaidPage />} />

                  <Route path="/myaccount" element={<MyAccountPage />} />
                  <Route path="/myaccount/address" element={<MyAccountAddressPage />} />
                  <Route path="/myaccount/changepassword" element={<MyAccountChangePasswordPage />} />
                  <Route path="/myaccount/customerinfo" element={<MyAccountCustomerInfoPage />} />

                  <Route path="/become-a-supplier" element={<BecomeASupplierPage />} />
                  <Route path="/craft-business-hub" element={<CraftBusinessHubPage />} />
                  <Route path="/partner-directory" element={<PartnerDirectoryPage />} />
                  <Route path="/contract-co-packing-directory" element={<ContractCoPackingDirectoryPage />} />
                  <Route path="/champions-program" element={<ChampionsProgramPage />} />
                  {/* Hidden internal backup — Champions Program Backup - May 2026 */}
                  <Route path="/champions-program-backup" element={<ChampionsProgramBackupPage />} />
                  <Route path="/events" element={<EventsPage />} />
                  <Route path="/blog" element={<BlogPage />} />
                  <Route path="/blog/:slug" element={<BlogPostPage />} />
                  <Route path="/portal/login" element={<PortalLoginPage />} />
                  <Route path="/portal/register" element={<PortalRegisterPage />} />
                  <Route path="/portal/forgot-password" element={<PortalForgotPasswordPage />} />
                  <Route path="/portal/reset-password" element={<PortalResetPasswordPage />} />
                  {/*
                    Public route: the page itself verifies the magic-link
                    token and gates the form on an active session, so the
                    auth guard would only get in the way of first-time
                    temp-access sign-ins.
                  */}
                  <Route
                    path="/portal/change-password"
                    element={<PortalChangePasswordPage />}
                  />

                  {/* Admin auth pages keep their own minimal chrome */}
                  <Route path="/admin/login" element={<AdminLoginPage />} />
                  <Route path="/admin/setup" element={<AdminSetupPage />} />
                </Route>

                {/* Authenticated portal — uses its own sidebar shell, no global Navbar/Footer */}
                <Route element={<PortalShellLayout />}>
                  <Route path="/portal/dashboard" element={<PortalHomePage />} />
                  <Route path="/portal/home" element={<PortalHomePage />} />
                  <Route path="/portal/profile" element={<PortalProfilePage />} />
                  <Route path="/portal/files" element={<Navigate to="/portal/reports" replace />} />
                  <Route path="/portal/orders" element={<PortalOrdersPage />} />
                  <Route path="/portal/services" element={<PortalServicesPage />} />
                  <Route path="/portal/applications" element={<PortalApplicationsPage />} />
                  <Route path="/portal/reports" element={<PortalReportsPage />} />
                  <Route path="/portal/directory" element={<PortalDirectoryPage />} />
                  <Route path="/portal/co-marketing" element={<PortalCoMarketingPage />} />
                  <Route path="/portal/settings" element={<PortalSettingsPage />} />
                  <Route path="/portal/go-live" element={<PortalGoLivePage />} />
                  <Route path="/portal/temp-preview" element={<PortalTempPreviewPage />} />
                  <Route path="/portal/legacy-tabs" element={<PortalDashboardPage />} />
                  <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
                  <Route path="/admin/blog" element={<AdminBlogPage />} />
                  <Route path="/admin/directory" element={<AdminDirectoryPage />} />
                  <Route path="/admin/users" element={<AdminUsersPage />} />
                  <Route path="/admin/popups" element={<AdminPopupsPage />} />
                </Route>
              </Routes>
              </Suspense>
            </BrowserRouter>
            </PortalAuthProvider>
          </ThemeProvider>
        </HydrationBoundary>
      </QueryClientProvider>
    </Provider>
  );
}
